import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, Printer, PencilSimple, Eye } from "@phosphor-icons/react";
import ShareButton from "@/components/ShareButton";
import ExportPdfButton from "@/components/ExportPdfButton";
import { BrandHeader, BrandFooter } from "@/components/Brand";
import CccBlueprint from "@/components/blueprints/CccBlueprint";

export default function CccTracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const res = await api.get(`/documents/${id}`); setDoc(res.data); }
    catch { toast.error("Document not found"); navigate("/dashboard"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const toggle = async (taskId, current) => {
    setDoc((prev) => {
      const payload = { ...prev.payload, countermeasures: prev.payload.countermeasures.map((t) => (t.id === taskId ? { ...t, status: !current } : t)) };
      return { ...prev, payload };
    });
    try {
      const res = await api.patch(`/documents/${id}/task`, { taskId, status: !current });
      setDoc((prev) => ({ ...prev, status: res.data.status, progress: res.data.progress }));
    } catch { toast.error("Update failed"); load(); }
  };

  if (loading || !doc)
    return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto w-full py-8 px-6 lg:px-10 print:px-0 print:py-0 print:max-w-none">
        <div className="flex flex-wrap items-start justify-between gap-4 print:hidden">
          <div>
            <button data-testid="back-to-dashboard" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-2"><ArrowLeft size={16} /> Workspace</button>
            <p className="text-xs font-semibold tracking-[0.12em] uppercase text-blue-700">Concern · Cause · Countermeasure</p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{doc.title}</h1>
            {doc.views_count > 0 && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-400" data-testid="doc-views">
                <Eye size={14} /> Viewed {doc.views_count} {doc.views_count === 1 ? "time" : "times"} via shared link
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ShareButton docId={id} initialToken={doc.share_token} />
            <ExportPdfButton docId={id} />
            <button data-testid="edit-ccc-button" onClick={() => navigate(`/ccc/${id}`)} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><PencilSimple size={16} /> Edit</button>
            <button data-testid="export-pdf-button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-sm bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"><Printer size={18} /> Print / PDF</button>
          </div>
        </div>

        <div className="mt-6"><BrandHeader branding={settings} /></div>
        <div className="hidden print:block mb-4">
          <p className="text-xs font-semibold tracking-[0.12em] uppercase text-blue-700">Concern · Cause · Countermeasure</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">{doc.title}</h1>
        </div>
        <CccBlueprint doc={doc} onToggle={toggle} />
        <BrandFooter branding={settings} fallback="MODUS · Concern · Cause · Countermeasure" />
      </div>
    </Layout>
  );
}
